import React, { useMemo } from 'react'
import withTranslator from '../HOCComponents/withTranslator'
import styled from 'styled-components'
import moment from 'moment'
import first from 'lodash/first'
import map from 'lodash/map'
import truncate from 'lodash/truncate'
import capitalize from 'lodash/capitalize'
import { UIStyles } from '../../theme'
import themeColors from '@layers-frontend/commons/styles/themeColors'
import { Avatar, Chip } from '@mui/material'

import SampleFormWrapperContainer from '../../containers/FieldsContainers/SampleFormContainers/SampleFormWrapperContainer'
import SamplesFilter from './SamplesFilter'

import { trackEvent } from '../analytics/analytics'
import { TRACK_EVENTS } from '../../constants'

const SampleWrapper = styled.div`
  color: white;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: flex-start;
  padding: 1rem;
  border-bottom: 1px solid ${UIStyles.mintBorder};
  position: relative;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${themeColors.whiteTransparent01};
  }
`

const Notes = styled.p`
  grid-column: 2 / 3;
  font-size: 1.3rem;
  margin: 0;
`

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  align-self: center;
  margin-left: 2rem;
`

const DateAndUniqueId = styled.p`
  margin: 0;
  font-size: 1.3rem;
`

const UnstiledButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border: 0;
  outline: none;
  background-color: transparent;
  z-index: 1;
  width: 100%;
`

// Sample type tag: the navy and mint contour of the map buttons and tooltips.
const chipStyle = {
  margin: 0,
  backgroundColor: UIStyles.deepblueColor3,
  boxShadow: `inset 0 0 0 1px ${UIStyles.mintBorder}`
}

const avatarStyle = {
  borderColor: UIStyles.mintBorder,
  borderWidth: '1px',
  borderStyle: 'solid',
  backgroundColor: 'transparent'
}

const NoResultsMessage = styled.div`
  color: ${UIStyles.lightGrey};
  text-align: center;
  padding: 2rem;
  font-size: 1.3rem;
`

const MainContainer = styled.div`
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-color: ${themeColors.transparent};
`

const FixedTopSection = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
`

const FilterSection = styled.div`
  margin-bottom: 0.5rem;
`

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 1rem;
  border-top: 1px solid ${UIStyles.mintBorder};
`

function SamplesList({ t, samples, selectSampleById, isSampleFormOpen, filterSamples, filteredSamples }) {
  const handleClickedSample = sample => {
    selectSampleById(sample.id)
    trackEvent(TRACK_EVENTS.FIELD_PANEL_SAMPLE_ITEM, {
      value: sample.id,
      button: 'Sample item clicked',
      location: TRACK_EVENTS.FIELD_PANEL
    })
  }

  const handleFilterChange = event => {
    const value = event.target.value

    if (!value) {
      filterSamples([])
      return
    }

    const searchTerms = value.split(',').map(term => term.trim().toLowerCase())
    const matchingSamples = samples.filter(sample => {
      const sampleValues = [
        sample.notes,
        sample.uniqueId,
        sample.externalId?.toString(),
        t(sample?.type?.name),
        moment(sample.sampleCreationDate).format('DD-MM-YYYY'),
        moment(sample.snapshotDate).format('DD-MM-YYYY')
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return searchTerms.every(term => sampleValues.includes(term))
    })

    const filteredSamples =
      matchingSamples.length > 0 ? matchingSamples.map(sample => ({ ...sample, searchTerm: value })) : [{ searchTerm: value, noMatches: true }]

    filterSamples(filteredSamples)
  }

  const displayedSamples = useMemo(() => {
    if (!filteredSamples || !Array.isArray(filteredSamples)) return samples
    if (filteredSamples.length > 0 && !filteredSamples[0].noMatches) {
      return filteredSamples
    }

    if (filteredSamples.length > 0 && filteredSamples[0].noMatches) {
      return []
    }

    return samples
  }, [samples, filteredSamples])

  return (
    <MainContainer>
      <FixedTopSection>
        <SampleFormWrapperContainer />
        {!isSampleFormOpen && (
          <FilterSection>
            <SamplesFilter t={t} onFilterChange={handleFilterChange} value={filteredSamples?.[0]?.searchTerm || ''} />
          </FilterSection>
        )}
      </FixedTopSection>
      {!isSampleFormOpen && (
        <ScrollableContent>
          {filteredSamples?.length > 0 && filteredSamples[0]?.noMatches ? (
            <NoResultsMessage>{t('No results found')}</NoResultsMessage>
          ) : (
            map(displayedSamples, sample => (
              <SampleWrapper key={sample.id}>
                <UnstiledButton onClick={() => handleClickedSample(sample)} />
                <Chip
                  label={capitalize(t(sample?.type?.name))}
                  key={sample.id}
                  style={chipStyle}
                  avatar={
                    <Avatar style={avatarStyle} size={32}>
                      {first(capitalize(t(sample?.type?.name)))}
                    </Avatar>
                  }
                />
                <DescriptionWrapper>
                  <DateAndUniqueId>
                    {moment(sample.sampleCreationDate).format('DD-MM-YYYY')} - {sample.uniqueId || sample.externalId}
                  </DateAndUniqueId>
                  {sample.notes ? <Notes>{truncate(sample.notes, { length: 28, omission: '...' })}</Notes> : null}
                </DescriptionWrapper>
              </SampleWrapper>
            ))
          )}
        </ScrollableContent>
      )}
    </MainContainer>
  )
}

export default withTranslator(SamplesList)
